import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { FormField, Switch } from '../../components/FormField.jsx';
import { PageHeader } from '../../components/PageHeader.jsx';
import { Modal, ConfirmDialog, submitForm } from '../../components/Modal.jsx';
import { EmptyState } from './Teams.jsx';

const empty = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  position: '',
  jerseyNumber: '',
  teamIds: [],
  emergencyContactName: '',
  emergencyContactPhone: '',
  active: true,
};

export default function Players() {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filterTeam, setFilterTeam] = useState('');
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function refresh() {
    const [p, t] = await Promise.all([Admin.players.list(filterTeam), Admin.teams.list()]);
    setPlayers(p.players);
    setTeams(t.teams);
  }
  useEffect(() => { refresh(); }, [filterTeam]);

  function openCreate() { setForm(empty); setErr(null); setModal('create'); }
  function openEdit(p) {
    setForm({
      firstName: p.firstName, lastName: p.lastName, email: p.email || '', phone: p.phone || '',
      position: p.position || '', jerseyNumber: p.jerseyNumber ?? '',
      teamIds: p.teamIds || [], emergencyContactName: p.emergencyContactName || '',
      emergencyContactPhone: p.emergencyContactPhone || '', active: p.active,
    });
    setErr(null);
    setModal({ id: p.id });
  }
  async function save(e) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const payload = { ...form, jerseyNumber: form.jerseyNumber === '' ? undefined : Number(form.jerseyNumber) };
      if (modal === 'create') await Admin.players.create(payload);
      else await Admin.players.update(modal.id, payload);
      setModal(null);
      await refresh();
    } catch (e) {
      setErr(e.data?.issues ? e.data.issues.map((i) => i.message).join(', ') : e.message);
    } finally { setBusy(false); }
  }
  async function remove(id) { await Admin.players.remove(id); await refresh(); }
  const toggleTeam = (id) => setForm((f) => ({
    ...f,
    teamIds: f.teamIds.includes(id) ? f.teamIds.filter((x) => x !== id) : [...f.teamIds, id],
  }));

  return (
    <div>
      <PageHeader
        eyebrow="Roster"
        title="Players"
        description="Your squad. Assign players to multiple sides as needed."
        action={<button onClick={openCreate} className="btn-primary btn-sm">+ New player</button>}
      />
      <div className="flex items-center gap-3 mb-5">
        <select className="input max-w-xs" value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)}>
          <option value="">All teams</option>
          {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <span className="text-xs text-ink-3">{players.length} {players.length === 1 ? 'player' : 'players'}</span>
      </div>

      {players.length === 0 ? (
        <EmptyState onAction={openCreate} label="Add a player" />
      ) : (
        <div className="card-flush">
          <table className="hidden md:table w-full">
            <thead>
              <tr className="border-b border-line">
                <th className="t-head text-left px-5 py-3">Name</th>
                <th className="t-head text-left px-5 py-3">#</th>
                <th className="t-head text-left px-5 py-3">Position</th>
                <th className="t-head text-left px-5 py-3">Teams</th>
                <th className="t-head text-right px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => (
                <tr key={p.id} className={`border-b border-line last:border-0 hover:bg-line-soft/30 ${!p.active ? 'opacity-60' : ''}`}>
                  <td className="px-5 py-3 text-sm font-medium">{p.firstName} {p.lastName}</td>
                  <td className="px-5 py-3 numeric text-sm">{p.jerseyNumber ?? ''}</td>
                  <td className="px-5 py-3 text-sm text-ink-2">{p.position || '—'}</td>
                  <td className="px-5 py-3 text-xs text-ink-2">
                    {(p.teamIds || []).map((id) => teams.find((t) => t.id === id)?.name).filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-5 py-3 text-right space-x-1">
                    <button className="btn-outline btn-xs" onClick={() => openEdit(p)}>Edit</button>
                    <button className="btn-ghost btn-xs text-danger" onClick={() => setConfirm(p)}>Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="md:hidden divide-rule">
            {players.map((p) => (
              <div key={p.id} className={`p-4 ${!p.active ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      {p.jerseyNumber != null && <span className="numeric text-xl">{p.jerseyNumber}</span>}
                      <span className="font-medium">{p.firstName} {p.lastName}</span>
                    </div>
                    <div className="text-xs text-ink-2 mt-1">
                      {p.position || '—'} · {(p.teamIds || []).map((id) => teams.find((t) => t.id === id)?.name).filter(Boolean).join(' · ') || 'No team'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button className="btn-outline btn-xs" onClick={() => openEdit(p)}>Edit</button>
                    <button className="btn-ghost btn-xs text-danger" onClick={() => setConfirm(p)}>Del</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        eyebrow={modal === 'create' ? 'New' : 'Edit'}
        title={modal === 'create' ? 'Add player' : `${form.firstName} ${form.lastName}`}
        size="lg"
        footer={
          <>
            <button onClick={() => setModal(null)} className="btn-ghost" type="button">Cancel</button>
            <button type="button" onClick={() => submitForm('player-form')} className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        {err && <div className="mb-4 px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{err}</div>}
        <form id="player-form" onSubmit={save} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="First name" required>
              <input className="input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required autoFocus />
            </FormField>
            <FormField label="Last name" required>
              <input className="input" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} required />
            </FormField>
            <FormField label="Email">
              <input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </FormField>
            <FormField label="Phone">
              <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </FormField>
            <FormField label="Position">
              <input className="input" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
            </FormField>
            <FormField label="Jersey number">
              <input type="number" min={0} max={99} className="input" value={form.jerseyNumber} onChange={(e) => setForm({ ...form, jerseyNumber: e.target.value })} />
            </FormField>
          </div>
          <div>
            <div className="label">Teams</div>
            <div className="flex flex-wrap gap-2">
              {teams.map((t) => {
                const on = form.teamIds.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggleTeam(t.id)}
                    className={`chip ${on ? 'bg-brand text-white' : 'bg-line-soft text-ink-2 hover:bg-line'}`}
                  >
                    {t.name}
                  </button>
                );
              })}
              {!teams.length && <span className="text-xs text-ink-3">No teams yet — create one first.</span>}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-line">
            <FormField label="Emergency contact name">
              <input className="input" value={form.emergencyContactName} onChange={(e) => setForm({ ...form, emergencyContactName: e.target.value })} />
            </FormField>
            <FormField label="Emergency contact phone">
              <input className="input" value={form.emergencyContactPhone} onChange={(e) => setForm({ ...form, emergencyContactPhone: e.target.value })} />
            </FormField>
          </div>
          <Switch checked={form.active} onChange={(v) => setForm({ ...form, active: v })} label="Active on roster" />
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => remove(confirm.id)}
        title={`Delete ${confirm?.firstName} ${confirm?.lastName}?`}
        message="This removes the player record entirely."
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}
